import { DASH } from 'modules/constants'
import formatNumber from 'modules/utils/numbers/formatNumber'
import { useTranslation } from 'react-i18next'
import { removeSeconds } from 'views/pages/LogisticsKR/helpers'
import { LogisticsKRLoadingWarehouse, LogisticsKRUnloadingWarehouse } from 'views/pages/LogisticsKR/types'
import { Address, YesNoUnknown } from '../types'

type PossibleAddress = Address | LogisticsKRLoadingWarehouse | LogisticsKRUnloadingWarehouse
type Formatter = (address: PossibleAddress) => string | undefined

type AddressFormatter = (address: PossibleAddress, keys: string[]) => unknown[]

export const useAddressFormatter = ({ emptyString = DASH } = {}) => {
  const { t } = useTranslation(['deal', 'warehouse', 'car', 'address'])

  const warehouseYNU = (value: YesNoUnknown | boolean) => {
    if (typeof value === 'boolean') {
      switch (value) {
        case true:
          return t('warehouse:yesNoUnknown.yes')
        case false:
          return t('warehouse:yesNoUnknown.no')
        default:
          return t('warehouse:yesNoUnknown.unknown')
      }
    }
    return t(`warehouse:yesNoUnknown.${value}`)
  }

  const volumePerDay = (a: PossibleAddress) => {
    if (!a.volume_per_day) {
      return emptyString
    }
    let res = `${formatNumber(a.volume_per_day)} ${t('common:tDay')}`
    if ('timeslot_required' in a && a.timeslot_required) {
      res += `(${t('address:fields.timeslot_required')})`
    }
    return res
  }

  const formatters: Record<string, Formatter> = {
    car_delivery: (a) => warehouseYNU(a.car_delivery as YesNoUnknown | boolean),
    gates_height: (a) => (a.gates_height ? `${formatNumber(a.gates_height)} ${t('common:m')}` : emptyString),
    is_dump_truck: (a) =>
      'is_dump_truck' in a && a.is_dump_truck ? t('car:is_dump_truck') : t('car:is_not_dump_truck'),
    loader_type: (a) =>
      ('loader_type' in a && a.loader_type?.map((type) => t(`vehicle_type.${type}`)).join(', ')) || emptyString,
    railway_delivery: (a) => warehouseYNU(a.railway_delivery as YesNoUnknown | boolean),
    scales_capacity: (a) => (a.scales_capacity ? `${formatNumber(a.scales_capacity)} ${t('common:t')}` : emptyString),
    scales_length: (a) => (a.scales_length ? `${a.scales_length} ${t('common:m')}` : emptyString),
    scales_remoteness: (a) =>
      a.scales_remoteness ? `${formatNumber(a.scales_remoteness)} ${t('common:km')}` : emptyString,
    overload: (a) => t(`address:overLoadValues.${a.overload}`),
    schedule: (a) =>
      a.schedule_from && a.schedule_to
        ? `${removeSeconds(a.schedule_from)}-${removeSeconds(a.schedule_to)}`
        : emptyString,
    unloading_type: (a) =>
      'unloading_type' in a && a.unloading_type ? t(`directions.${a.unloading_type}`) : emptyString,
    vehicle_type: (a) => a.vehicle_type?.map((type) => t(`vehicle_type.${type}`)).join(', ') || emptyString,
    volume_per_day: volumePerDay,
    works_on_weekend: (a) => warehouseYNU(a.works_on_weekend as YesNoUnknown | boolean),
    weather_conditions: (a) => warehouseYNU(a.weather_conditions),
  }

  const formatter: AddressFormatter = (address, keys) => keys.map((key) => formatters[key]?.(address))

  return formatter
}

export const getAddressKeysByType = (isLoadType: boolean): string[] =>
  isLoadType
    ? [
        'gates_height',
        'loader_type',
        'scales_capacity',
        'scales_remoteness',
        'scales_length',
        'volume_per_day',
        'weather_conditions',
        'overload',
        'vehicle_type',
        'schedule',
        'works_on_weekend',
        'car_delivery',
        'railway_delivery',
      ]
    : [
        'vehicle_type',
        'is_dump_truck',
        'unloading_type',
        'volume_per_day',
        'weather_conditions',
        'overload',
        'schedule',
        'works_on_weekend',
        'car_delivery',
        'railway_delivery',
      ]
